import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { MapPin, Plus, Edit, Trash2, Archive, CheckCircle, XCircle, Search, Globe, Tag, Loader2, Download, Upload, FileJson, FileSpreadsheet, FileText, ChevronDown, RefreshCw, Users, Building2, Briefcase, Shield } from 'lucide-react';
import BackButton from '../../components/BackButton';
import { useTerritoires } from './useTerritoires';
import TerritoiresModals from './TerritoiresModals';

export default function TerritoiresManagement({ getAuthHeader }) {
  const ctx = useTerritoires(getAuthHeader);
  const { territoires, loading, dispositifs, pays, selectedTerritoires, backendStats, fileInputRef, filterActif, setFilterActif, filterPays, setFilterPays, filterDispositif, setFilterDispositif, searchQuery, setSearchQuery, showArchived, setShowArchived, fetchInitialData, handleCreateNew, handleEdit, handleDelete, handleBulkAction, handleSelectAll, handleSelectTerritoire, filteredTerritoires, territoireStats, exportToJSON, exportToCSV, exportToExcel, handleFileImport } = ctx;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        
      {/* Bouton Retour */}
      <BackButton label="Retour" />

<Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }


  return (
    <div className="space-y-6">
      {/* Hero Header — Ultra Pro */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-900 to-blue-900 p-6 lg:p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(99,102,241,0.4),_transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(56,189,248,0.25),_transparent_45%)] pointer-events-none" />
        <div className="relative">
          <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-indigo-500/40 flex-shrink-0">
                <Globe className="h-7 w-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/30 backdrop-blur-sm font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse" />
                    Données live
                  </Badge>
                  <Badge variant="outline" className="bg-white text-slate-800/10 text-white border-white/20 backdrop-blur-sm font-semibold">
                    {territoireStats.total} territoires
                  </Badge>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold flex items-center gap-2">
                  Gestion des Territoires Éligibles
                </h2>
                <p className="text-indigo-100 mt-1 text-sm lg:text-base">
                  {filteredTerritoires.length} affiché(s) • {selectedTerritoires.length} sélectionné(s) • Couverture moyenne : <span className="font-bold text-cyan-300">{territoireStats.couvertureMoyenne}</span> dispositif(s)/territoire
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="relative group">
                <Button variant="outline" className="flex items-center gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm">
                  <Download className="h-4 w-4" />
                  Exporter
                  <ChevronDown className="h-3 w-3" />
                </Button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50 hidden group-hover:block">
                  <button onClick={exportToJSON} className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-700">
                    <FileJson className="h-4 w-4 text-amber-600" /> Export JSON
                  </button>
                  <button onClick={exportToCSV} className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-700">
                    <FileText className="h-4 w-4 text-green-600" /> Export CSV
                  </button>
                  <button onClick={exportToExcel} className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-700">
                    <FileSpreadsheet className="h-4 w-4 text-blue-600" /> Export Excel
                  </button>
                </div>
              </div>
              <Button variant="outline" className="flex items-center gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4" /> Importer
              </Button>
              <input type="file" ref={fileInputRef} onChange={handleFileImport} accept=".json,.csv" className="hidden" />
              <Button onClick={() => setShowArchived(!showArchived)} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm">
                <Archive className="mr-2 h-4 w-4" />
                {showArchived ? 'Masquer archivés' : 'Voir archivés'}
              </Button>
              <Button onClick={() => fetchInitialData()} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm" data-testid="territoires-refresh">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button onClick={handleCreateNew} className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white border-0 shadow-lg shadow-cyan-500/30" data-testid="territoires-create">
                <Plus className="mr-2 h-4 w-4" />
                Nouveau Territoire
              </Button>
            </div>
          </div>

          {/* KPIs dynamiques */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6">
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4 hover:bg-white/15 transition-colors">
              <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold uppercase tracking-wider">
                <CheckCircle className="h-3.5 w-3.5" /> Actifs
              </div>
              <p className="text-3xl font-bold mt-1">{territoireStats.actifs}</p>
              <p className="text-xs text-indigo-200 mt-0.5">sur {territoireStats.total} total</p>
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4 hover:bg-white/15 transition-colors">
              <div className="flex items-center gap-2 text-orange-300 text-xs font-semibold uppercase tracking-wider">
                <XCircle className="h-3.5 w-3.5" /> Inactifs
              </div>
              <p className="text-3xl font-bold mt-1">{territoireStats.inactifs}</p>
              <p className="text-xs text-indigo-200 mt-0.5">à réactiver / désactiver</p>
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4 hover:bg-white/15 transition-colors">
              <div className="flex items-center gap-2 text-purple-300 text-xs font-semibold uppercase tracking-wider">
                <Archive className="h-3.5 w-3.5" /> Archivés
              </div>
              <p className="text-3xl font-bold mt-1">{territoireStats.archives}</p>
              <p className="text-xs text-indigo-200 mt-0.5">historique conservé</p>
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4 hover:bg-white/15 transition-colors">
              <div className="flex items-center gap-2 text-cyan-300 text-xs font-semibold uppercase tracking-wider">
                <Globe className="h-3.5 w-3.5" /> Pays couverts
              </div>
              <p className="text-3xl font-bold mt-1">{territoireStats.paysCount}</p>
              <p className="text-xs text-indigo-200 mt-0.5">juridictions distinctes</p>
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4 hover:bg-white/15 transition-colors">
              <div className="flex items-center gap-2 text-yellow-300 text-xs font-semibold uppercase tracking-wider">
                <Tag className="h-3.5 w-3.5" /> Dispositifs
              </div>
              <p className="text-3xl font-bold mt-1">{territoireStats.dispositifsCovered}</p>
              <p className="text-xs text-indigo-200 mt-0.5">{dispositifs.length} disponibles</p>
            </div>
          </div>

          {/* Réseau live (backend) */}
          {backendStats?.global && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              <div className="rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-700/20 backdrop-blur-sm border border-blue-400/30 p-3 hover:scale-[1.02] transition-transform">
                <div className="flex items-center gap-2 text-blue-200 text-xs font-semibold uppercase tracking-wider">
                  <Users className="h-3.5 w-3.5" /> Membres réseau
                </div>
                <p className="text-2xl font-bold mt-1">{backendStats.global.total_membres}</p>
                <p className="text-xs text-blue-100 mt-0.5">tous territoires confondus</p>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-700/20 backdrop-blur-sm border border-purple-400/30 p-3 hover:scale-[1.02] transition-transform">
                <div className="flex items-center gap-2 text-purple-200 text-xs font-semibold uppercase tracking-wider">
                  <Building2 className="h-3.5 w-3.5" /> COOPER&apos;S
                </div>
                <p className="text-2xl font-bold mt-1">{backendStats.global.total_coopers}</p>
                <p className="text-xs text-purple-100 mt-0.5">partenaires actifs</p>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-700/20 backdrop-blur-sm border border-orange-400/30 p-3 hover:scale-[1.02] transition-transform">
                <div className="flex items-center gap-2 text-orange-200 text-xs font-semibold uppercase tracking-wider">
                  <Briefcase className="h-3.5 w-3.5" /> Experts
                </div>
                <p className="text-2xl font-bold mt-1">{backendStats.global.total_experts}</p>
                <p className="text-xs text-orange-100 mt-0.5">cabinets agréés</p>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-700/20 backdrop-blur-sm border border-emerald-400/30 p-3 hover:scale-[1.02] transition-transform">
                <div className="flex items-center gap-2 text-emerald-200 text-xs font-semibold uppercase tracking-wider">
                  <Shield className="h-3.5 w-3.5" /> Souscriptions
                </div>
                <p className="text-2xl font-bold mt-1">{backendStats.global.total_souscriptions_domium}</p>
                <p className="text-xs text-emerald-100 mt-0.5">DOMIUM total · {backendStats.global.total_ftpe_adhesions_actives} adhésions FTPE</p>
              </div>
            </div>
          )}

          {/* Top pays */}
          {territoireStats.topPays.length > 0 && (
            <div className="mt-5 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-indigo-200 mb-3">Top juridictions actives</p>
              <div className="flex flex-wrap gap-2">
                {territoireStats.topPays.map(p => (
                  <button
                    key={p.code}
                    onClick={() => setFilterPays(p.code)}
                    className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm bg-white/10 hover:bg-white/20 border border-white/20 transition-all"
                    data-testid={`territoires-top-pays-${p.code}`}
                  >
                    <span className="font-mono text-xs text-cyan-300">{p.code}</span>
                    <span className="font-medium">{p.nom}</span>
                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-200 text-xs font-bold">{p.actifs}</span>
                    <span className="text-indigo-300 text-xs">/ {p.count}</span>
                    {p.souscriptions > 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-200 text-xs font-bold">
                        {p.souscriptions} sub.
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions en masse */}
      {selectedTerritoires.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-blue-900">
                {selectedTerritoires.length} territoire(s) sélectionné(s)
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleBulkAction('activer')}
                  size="sm"
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-50"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Activer
                </Button>
                <Button
                  onClick={() => handleBulkAction('desactiver')}
                  size="sm"
                  variant="outline"
                  className="border-orange-600 text-orange-600 hover:bg-orange-50"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Désactiver
                </Button>
                <Button
                  onClick={() => handleBulkAction('archiver')}
                  size="sm"
                  variant="outline"
                  className="border-purple-600 text-purple-600 hover:bg-purple-50"
                >
                  <Archive className="mr-2 h-4 w-4" />
                  Archiver
                </Button>
                <Button
                  onClick={() => handleBulkAction('supprimer')}
                  size="sm"
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtre Statut */}
            <select
              value={filterActif}
              onChange={(e) => setFilterActif(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="actif">Actifs uniquement</option>
              <option value="inactif">Inactifs uniquement</option>
            </select>

            {/* Filtre Pays */}
            <select
              value={filterPays}
              onChange={(e) => setFilterPays(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les pays</option>
              {pays.map(p => (
                <option key={p.code} value={p.code}>{p.nom}</option>
              ))}
            </select>

            {/* Filtre Dispositif */}
            <select
              value={filterDispositif}
              onChange={(e) => setFilterDispositif(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les dispositifs</option>
              {dispositifs.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Liste des territoires */}
      <Card>
        <CardContent className="pt-6">
          {filteredTerritoires.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="mx-auto h-16 w-16 text-slate-300 mb-4" />
              <p className="text-slate-600 text-lg">Aucun territoire trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4">
                      <input
                        type="checkbox"
                        checked={selectedTerritoires.length === filteredTerritoires.length}
                        onChange={handleSelectAll}
                        className="rounded border-slate-300"
                      />
                    </th>
                    <th className="text-left p-4 font-semibold">Territoire</th>
                    <th className="text-left p-4 font-semibold">Code</th>
                    <th className="text-left p-4 font-semibold">Pays</th>
                    <th className="text-left p-4 font-semibold">Dispositifs</th>
                    <th className="text-left p-4 font-semibold">Statut</th>
                    <th className="text-right p-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTerritoires.map(territoire => (
                    <tr key={territoire.id} className="border-b hover:bg-slate-50">
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selectedTerritoires.includes(territoire.id)}
                          onChange={() => handleSelectTerritoire(territoire.id)}
                          className="rounded border-slate-300"
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-blue-600" />
                          <span className="font-medium">{territoire.nom}</span>
                          {territoire.archive && (
                            <Badge className="bg-purple-700 text-white font-semibold">
                              Archivé
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className="bg-slate-700 text-white font-semibold">{territoire.code}</Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-slate-400" />
                          <span className="text-sm">{territoire.pays_nom}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {territoire.dispositifs && territoire.dispositifs.length > 0 ? (
                            territoire.dispositifs.map(d => (
                              <Badge key={d} className="bg-blue-600 text-white text-xs font-semibold">
                                {d}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-xs text-slate-400">Aucun</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        {territoire.actif ? (
                          <Badge className="bg-green-600 text-white font-semibold">
                            <CheckCircle className="mr-1 h-3 w-3" />
                            Actif
                          </Badge>
                        ) : (
                          <Badge className="bg-red-600 text-white font-semibold">
                            <XCircle className="mr-1 h-3 w-3" />
                            Inactif
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 justify-end">
                          <Button
                            onClick={() => handleEdit(territoire)}
                            size="sm"
                            variant="outline"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(territoire.id)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <TerritoiresModals ctx={ctx} />
    </div>
  );
}
